import { emit, isEmbedded as packageIsEmbedded, resolveParentOrigin } from "@mediatechgroup/embed-bridge";

/**
 * Signals an embedded Club Central page sends to the website hosting it.
 *
 * The Integrations page documents these for clubs' web developers, and
 * public/embed.js turns each one into a `document` event of the same name
 * (with `{ type, ...detail }` as `e.detail`). A signal sent from a page that
 * is not inside an iframe goes nowhere, so this is a no-op outside an embed.
 *
 * Since the @mediatechgroup/embed-bridge adoption (PM a2640c12) a signal
 * travels as the package's envelope, `{ source: 'clubcentral', v: 1, type,
 * data }`, through `emit` to the host page's origin: a resize is `height`
 * with `{ px }`, the other two keep their names without the prefix. The
 * loader (generated from the package, scripts/generate-embed.js) reads the
 * envelope and, for this one release, the old `{ type, ...detail }` messages
 * too, so an old loader cached on a club's site and these frames never mix
 * in a way that drops a signal. The document events club developers listen
 * for are unchanged: same names, same `e.detail`. The pre-adoption
 * answers are pinned in src/utils/__tests__/embedBridge.pinned.test.ts and
 * src/utils/__tests__/embedLoader.pinned.test.ts.
 */
export type EmbedSignal =
  | "clubcentral:resize"
  | "clubcentral:complete"
  | "clubcentral:step-change";

/** The channel the frames stamp on every message; the loader reads only this one. */
export const EMBED_CHANNEL = "clubcentral";

/** The protocol type each signal travels as. */
const WIRE_TYPE: Record<EmbedSignal, string> = {
  "clubcentral:resize": "height",
  "clubcentral:complete": "complete",
  "clubcentral:step-change": "step-change",
};

export const isEmbedded = (): boolean => packageIsEmbedded();

export const postEmbedSignal = (
  type: EmbedSignal,
  detail: Record<string, unknown> = {}
): void => {
  if (!isEmbedded()) return;
  let data: Record<string, unknown> = detail;
  if (type === "clubcentral:resize") {
    // The resize event's wire field is `px`; the loader puts it back as `height`.
    const { height, ...rest } = detail;
    data = { ...rest, px: height };
  }
  try {
    emit({ type: WIRE_TYPE[type], data }, { source: EMBED_CHANNEL, targetOrigin: resolveParentOrigin() });
  } catch {
    // The host page is gone or refused the message. Nothing to do.
  }
};
